// Babysitter mini-game: handle the kids' needs at an average pace (safety first!)
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sound } from "@/lib/sound";

const NEEDS = [
  { id: "hungry", label: "Hungry!", emoji: "🍎", action: "Snack time", actionEmoji: "🍎" },
  { id: "sleepy", label: "Sleepy...", emoji: "😴", action: "Nap & story", actionEmoji: "📖" },
  { id: "play", label: "Bored!", emoji: "🧸", action: "Play a game", actionEmoji: "🎲" },
  { id: "potty", label: "Potty!", emoji: "🚽", action: "Potty break", actionEmoji: "🚽" },
  { id: "danger", label: "Climbing the stairs!", emoji: "⚠️", action: "Keep safe", actionEmoji: "🛡️" },
  { id: "sad", label: "Crying", emoji: "😢", action: "Comfort & hug", actionEmoji: "🤗" },
];
const GOAL = 6;
const CYCLE_MS = 4000; // relaxed pace

const BabysitGame = ({ onFinish }) => {
  const [need, setNeed] = useState(NEEDS[2]);
  const [score, setScore] = useState(0);
  const [misses, setMisses] = useState(0);
  const [time, setTime] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const timerRef = useRef();
  const cycleRef = useRef();
  const handled = score + misses;
  const done = handled >= GOAL;

  useEffect(() => {
    timerRef.current = setInterval(() => setTime((t) => t + 1), 1000);
    cycleRef.current = setInterval(() => setNeed(NEEDS[Math.floor(Math.random() * NEEDS.length)]), CYCLE_MS);
    return () => { clearInterval(timerRef.current); clearInterval(cycleRef.current); };
  }, []);

  useEffect(() => {
    if (!done) return;
    clearInterval(timerRef.current);
    clearInterval(cycleRef.current);
    const speedBonus = time <= 32 ? 1 : 0;
    const rating = Math.max(1, Math.min(5, score - misses + speedBonus));
    setTimeout(() => onFinish({ score, misses, rating }), 500);
  }, [done]); // eslint-disable-line

  const handleTap = (id) => {
    if (done) return;
    const correct = id === need.id;
    if (correct) {
      setScore((s) => s + 1);
      Sound.coin();
      setFeedback(need.id === "danger" ? "Safe! Great watching 🛡️" : "Yay! Happy kid 😊");
    } else {
      setMisses((m) => m + 1);
      Sound.error();
      setFeedback("Oops — not what they needed!");
    }
    setNeed(NEEDS[Math.floor(Math.random() * NEEDS.length)]);
    setTimeout(() => setFeedback(null), 800);
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex justify-between items-center mb-3 font-heading font-black flex-wrap gap-2">
        <div className="bg-violet-100 border-2 border-violet-300 rounded-xl px-3 py-1 text-violet-800" data-testid="baby-score">😊 {score}</div>
        <div className="bg-rose-100 border-2 border-rose-300 rounded-xl px-3 py-1 text-rose-800" data-testid="baby-misses">😢 {misses}</div>
        <div className="bg-emerald-100 border-2 border-emerald-300 rounded-xl px-3 py-1 text-emerald-800" data-testid="baby-progress">✅ {Math.min(handled, GOAL)}/{GOAL}</div>
        <div className="bg-amber-100 border-2 border-amber-300 rounded-xl px-3 py-1 text-amber-800">⏱ {time}s</div>
      </div>
      <p className="text-center text-xs text-slate-600 font-semibold mb-3">
        Take care of {GOAL} needs to finish the job. Watch for ⚠️ danger — safety comes first!
      </p>

      <div className={`rounded-3xl border-4 p-6 text-center mb-4 transition-colors ${need.id === "danger" ? "bg-rose-50 border-rose-400 animate-pulse" : "bg-violet-50 border-violet-300"}`} data-testid="baby-need">
        <div className="text-7xl mb-2 animate-bounce-slow">{need.emoji}</div>
        <div className="font-display text-2xl font-bold text-slate-900">{need.label}</div>
        <div className="h-5 mt-1 text-sm font-heading font-bold text-violet-700">{feedback}</div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {NEEDS.map((n) => (
          <Button
            key={n.id}
            onClick={() => handleTap(n.id)}
            data-testid={`baby-action-${n.id}`}
            className={`h-auto py-3 flex-col rounded-2xl btn-3d border-2 font-heading font-black text-xs ${
              n.id === "danger" ? "bg-rose-500 hover:bg-rose-600 border-rose-600" : "bg-violet-500 hover:bg-violet-600 border-violet-600"
            }`}
          >
            <span className="text-2xl">{n.actionEmoji}</span>
            {n.action}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default BabysitGame;
